import { useState, useEffect, useRef, Component } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  ArrowLeft, Plus, Trash2, Save, Users, Heart, 
  CircleDot, Square, Diamond, Loader2, Info, Download, AlertTriangle
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

// Error Boundary component to catch rendering errors
class GenogramErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Genogram Error Boundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen hero-gradient flex items-center justify-center p-6">
          <Card className="max-w-md w-full">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Unable to Load Genogram
              </h2>
              <p className="text-muted-foreground mb-4">
                We encountered an error loading the Family Genogram. This feature may not be available on your device.
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                Error: {this.state.error?.message || "Unknown error"}
              </p>
              <Button onClick={() => window.location.href = '/dashboard'}>
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

const GenogramPageContent = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const canvasRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [genogram, setGenogram] = useState({
    members: [],
    relationships: []
  });
  const [selectedMember, setSelectedMember] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMember, setNewMember] = useState({
    name: "",
    relationship: "self",
    generation: 0,
    gender: "female",
    birthYear: "",
    deathYear: "",
    notes: "",
    patterns: []
  });

  const relationshipTypes = [
    { id: "self", label: "You", generation: 0 },
    { id: "partner", label: "Partner/Spouse", generation: 0 },
    { id: "sibling", label: "Sibling", generation: 0 },
    { id: "mother", label: "Mother", generation: 1 },
    { id: "father", label: "Father", generation: 1 },
    { id: "maternal_grandmother", label: "Maternal Grandmother", generation: 2 },
    { id: "maternal_grandfather", label: "Maternal Grandfather", generation: 2 },
    { id: "paternal_grandmother", label: "Paternal Grandmother", generation: 2 },
    { id: "paternal_grandfather", label: "Paternal Grandfather", generation: 2 },
    { id: "child", label: "Child", generation: -1 },
    { id: "aunt_uncle", label: "Aunt/Uncle", generation: 1 },
    { id: "cousin", label: "Cousin", generation: 0 }
  ];

  const patternOptions = [
    "Addiction", "Anxiety", "Depression", "Abuse", "Divorce/Separation",
    "Financial Struggles", "Immigration", "Early Death", "Mental Illness",
    "Physical Illness", "Estrangement", "Success/Achievement", "Creativity",
    "Strong Faith", "Resilience", "Caregiving", "Workaholism"
  ];

  useEffect(() => {
    fetchGenogram();
  }, [token]);

  const fetchGenogram = async () => {
    try {
      setError(null);
      const response = await axios.get(`${API}/genogram`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        // Ensure members and relationships arrays exist
        setGenogram({
          ...response.data,
          members: response.data.members || [],
          relationships: response.data.relationships || []
        });
      }
    } catch (error) {
      // No genogram yet is OK, but log other errors
      if (error.response?.status !== 404) {
        console.error("Genogram fetch error:", error);
        setError("Unable to load genogram data. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = () => {
    if (!newMember.name.trim()) {
      toast.error("Please enter a name");
      return;
    }

    const relType = relationshipTypes.find(r => r.id === newMember.relationship);
    const member = {
      id: Date.now().toString(),
      ...newMember,
      generation: relType?.generation || 0
    };

    setGenogram(prev => ({
      ...prev,
      members: [...(prev?.members || []), member]
    }));

    setNewMember({
      name: "",
      relationship: "self",
      generation: 0,
      gender: "female",
      birthYear: "",
      deathYear: "",
      notes: "",
      patterns: []
    });
    setShowAddForm(false);
    toast.success(`Added ${member.name} to your family map`);
  };

  const handleDeleteMember = (memberId) => {
    setGenogram(prev => ({
      ...prev,
      members: (prev?.members || []).filter(m => m.id !== memberId)
    }));
    setSelectedMember(null);
    toast.success("Member removed");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Transform members to people format for backend
      const payload = {
        people: (genogram.members || []).map(m => ({
          id: m.id,
          name: m.name,
          birth_year: m.birthYear ? parseInt(m.birthYear) : null,
          death_year: m.deathYear ? parseInt(m.deathYear) : null,
          gender: m.gender,
          generation: m.generation,
          notes: m.notes || null,
          patterns: m.patterns || []
        })),
        relationships: genogram.relationships || [],
        patterns_noted: (genogram.members || []).flatMap(m => m.patterns || [])
      };
      
      await axios.post(`${API}/genogram`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Family map saved!");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save genogram");
    } finally {
      setSaving(false);
    }
  };

  const togglePattern = (pattern) => {
    setNewMember(prev => ({
      ...prev,
      patterns: prev.patterns.includes(pattern)
        ? prev.patterns.filter(p => p !== pattern)
        : [...prev.patterns, pattern]
    }));
  };

  const getMemberIcon = (gender) => {
    if (gender === "male") return <Square className="w-6 h-6" />;
    if (gender === "female") return <CircleDot className="w-6 h-6" />;
    return <Diamond className="w-6 h-6" />;
  };

  const getGenerationLabel = (gen) => {
    if (gen === 0) return "Your Generation";
    if (gen === 1) return "Parents' Generation";
    if (gen === 2) return "Grandparents' Generation";
    if (gen === -1) return "Children's Generation";
    return `Generation ${gen}`;
  };

  // Group members by generation - with null safety
  const membersByGeneration = (genogram?.members || []).reduce((acc, member) => {
    const gen = member.generation || 0;
    if (!acc[gen]) acc[gen] = [];
    acc[gen].push(member);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
        <div className="text-center">
          <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Unable to Load Genogram</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate("/dashboard")} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <Button onClick={() => { setLoading(true); fetchGenogram(); }}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10 border-b border-border/50">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <Users className="w-8 h-8 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-semibold text-foreground">
                  Family Genogram
                </h1>
                <p className="text-muted-foreground mt-1">
                  Map your family patterns across generations
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700 text-white rounded-xl"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Map
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8" data-testid="genogram-page">
        {/* Understanding Your Genogram */}
        <Card className="glass-card rounded-2xl border-0 mb-8 bg-amber-50/50 dark:bg-amber-950/20">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Info className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-medium text-foreground mb-2">Understanding Your Family Map</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  A genogram is a powerful tool for visualizing family patterns across generations. By mapping your family tree 
                  and noting recurring patterns (like anxiety, addiction, or caregiving), you can identify intergenerational 
                  themes that may be influencing your life today—and begin to consciously choose which patterns to continue or break.
                </p>
                
                {/* Legend */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-amber-200/50">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-pink-100 border-2 border-pink-400 flex items-center justify-center">
                      <span className="text-xs">♀</span>
                    </div>
                    <span className="text-xs text-muted-foreground">Female</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded bg-blue-100 border-2 border-blue-400 flex items-center justify-center">
                      <span className="text-xs">♂</span>
                    </div>
                    <span className="text-xs text-muted-foreground">Male</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rotate-45 bg-emerald-100 border-2 border-teal-400"></div>
                    <span className="text-xs text-muted-foreground">Other/Non-binary</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-1 bg-amber-100 rounded-full text-amber-700">Pattern</span>
                    <span className="text-xs text-muted-foreground">Family pattern tag</span>
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground mt-4 italic">
                  💡 Tip: Start with yourself (Generation 0), then add parents (Gen 1), grandparents (Gen 2), and children (Gen -1). 
                  Add patterns you've noticed in each person to see themes across generations.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add Member Button */}
        <div className="mb-8">
          <Button
            onClick={() => setShowAddForm(true)}
            className="rounded-xl"
            data-testid="add-member-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Family Member
          </Button>
        </div>

        {/* Add Member Form */}
        {showAddForm && (
          <Card className="glass-card rounded-2xl border-0 mb-8">
            <CardContent className="p-6">
              <h3 className="font-heading text-lg font-semibold mb-6">Add Family Member</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium mb-1 block">Name</label>
                  <Input
                    placeholder="Name or nickname..."
                    value={newMember.name}
                    onChange={(e) => setNewMember(prev => ({ ...prev, name: e.target.value }))}
                    className="rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-1 block">Relationship to You</label>
                  <select
                    value={newMember.relationship}
                    onChange={(e) => setNewMember(prev => ({ ...prev, relationship: e.target.value }))}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2"
                  >
                    {relationshipTypes.map(r => (
                      <option key={r.id} value={r.id}>{r.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-1 block">Gender</label>
                  <div className="flex gap-3">
                    {["female", "male", "other"].map(g => (
                      <button
                        key={g}
                        onClick={() => setNewMember(prev => ({ ...prev, gender: g }))}
                        className={`px-4 py-2 rounded-lg border transition-colors capitalize ${
                          newMember.gender === g 
                            ? 'border-primary bg-primary/10 text-primary' 
                            : 'border-input hover:border-primary/50'
                        }`}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Birth Year</label>
                    <Input
                      placeholder="e.g. 1950"
                      value={newMember.birthYear}
                      onChange={(e) => setNewMember(prev => ({ ...prev, birthYear: e.target.value }))}
                      className="rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Death Year (if applicable)</label>
                    <Input
                      placeholder="e.g. 2020"
                      value={newMember.deathYear}
                      onChange={(e) => setNewMember(prev => ({ ...prev, deathYear: e.target.value }))}
                      className="rounded-lg"
                    />
                  </div>
                </div>
              </div>
              
              <div className="mt-6">
                <label className="text-sm font-medium mb-2 block">Patterns Observed (select all that apply)</label>
                <div className="flex flex-wrap gap-2">
                  {patternOptions.map(pattern => (
                    <button
                      key={pattern}
                      onClick={() => togglePattern(pattern)}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        newMember.patterns.includes(pattern)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted hover:bg-muted/80'
                      }`}
                    >
                      {pattern}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="mt-6">
                <label className="text-sm font-medium mb-1 block">Notes</label>
                <textarea
                  placeholder="Any additional observations about this family member..."
                  value={newMember.notes}
                  onChange={(e) => setNewMember(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 min-h-[80px]"
                />
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowAddForm(false)} className="rounded-lg">
                  Cancel
                </Button>
                <Button onClick={handleAddMember} className="rounded-lg">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Member
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Genogram Visualization */}
        {(genogram?.members?.length || 0) === 0 ? (
          <Card className="glass-card rounded-2xl border-0">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                Start Your Family Map
              </h3>
              <p className="text-muted-foreground mb-6">
                Begin by adding yourself, then add parents, grandparents, and other family members.
              </p>
              <Button onClick={() => setShowAddForm(true)} className="rounded-xl">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Family Member
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Generation Rows */}
            {[2, 1, 0, -1].map(gen => {
              const members = membersByGeneration[gen] || [];
              if (members.length === 0) return null;
              
              return (
                <div key={gen}>
                  <h3 className="text-sm font-medium text-muted-foreground mb-4">
                    {getGenerationLabel(gen)}
                  </h3>
                  <div className="flex flex-wrap gap-4 justify-center">
                    {members.map(member => (
                      <Card 
                        key={member.id}
                        className={`rounded-2xl border-2 cursor-pointer transition-all hover:shadow-lg ${
                          selectedMember?.id === member.id 
                            ? 'border-primary ring-2 ring-primary/20' 
                            : 'border-transparent'
                        } ${member.deathYear ? 'opacity-70' : ''}`}
                        onClick={() => setSelectedMember(member)}
                      >
                        <CardContent className="p-4 text-center min-w-[140px]">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 ${
                            member.gender === 'female' ? 'bg-pink-100 text-pink-600' :
                            member.gender === 'male' ? 'bg-blue-100 text-blue-600' :
                            'bg-emerald-100 text-emerald-600'
                          }`}>
                            {getMemberIcon(member.gender)}
                          </div>
                          <p className="font-medium text-foreground">{member.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{member.relationship.replace('_', ' ')}</p>
                          {member.birthYear && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {member.birthYear}{member.deathYear ? ` - ${member.deathYear}` : ''}
                            </p>
                          )}
                          {member.patterns.length > 0 && (
                            <div className="flex flex-wrap justify-center gap-1 mt-2">
                              {member.patterns.slice(0, 2).map(p => (
                                <span key={p} className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full">
                                  {p}
                                </span>
                              ))}
                              {member.patterns.length > 2 && (
                                <span className="text-xs text-muted-foreground">+{member.patterns.length - 2}</span>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Selected Member Details */}
        {selectedMember && (
          <Card className="glass-card rounded-2xl border-0 mt-8">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-heading text-lg font-semibold">
                  {selectedMember.name}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteMember(selectedMember.id)}
                  className="text-red-500 hover:text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Remove
                </Button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Relationship:</span>
                  <span className="ml-2 capitalize">{selectedMember.relationship.replace('_', ' ')}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Gender:</span>
                  <span className="ml-2 capitalize">{selectedMember.gender}</span>
                </div>
                {selectedMember.birthYear && (
                  <div>
                    <span className="text-muted-foreground">Born:</span>
                    <span className="ml-2">{selectedMember.birthYear}</span>
                  </div>
                )}
                {selectedMember.deathYear && (
                  <div>
                    <span className="text-muted-foreground">Died:</span>
                    <span className="ml-2">{selectedMember.deathYear}</span>
                  </div>
                )}
              </div>
              
              {selectedMember.patterns.length > 0 && (
                <div className="mt-4">
                  <span className="text-sm text-muted-foreground block mb-2">Patterns:</span>
                  <div className="flex flex-wrap gap-2">
                    {selectedMember.patterns.map(p => (
                      <span key={p} className="text-sm bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-3 py-1 rounded-full">
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedMember.notes && (
                <div className="mt-4">
                  <span className="text-sm text-muted-foreground block mb-2">Notes:</span>
                  <p className="text-sm">{selectedMember.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Pattern Summary */}
        {(genogram?.members?.length || 0) > 0 && (
          <Card className="glass-card rounded-2xl border-0 mt-8">
            <CardContent className="p-6">
              <h3 className="font-heading text-lg font-semibold mb-4">
                Family Pattern Summary
              </h3>
              <div className="flex flex-wrap gap-3">
                {(() => {
                  const allPatterns = (genogram?.members || []).flatMap(m => m.patterns || []);
                  const patternCounts = allPatterns.reduce((acc, p) => {
                    acc[p] = (acc[p] || 0) + 1;
                    return acc;
                  }, {});
                  return Object.entries(patternCounts)
                    .sort(([,a], [,b]) => b - a)
                    .map(([pattern, count]) => (
                      <span key={pattern} className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 text-amber-800 dark:text-amber-300 px-4 py-2 rounded-full">
                        {pattern}
                        <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">
                          {count}
                        </span>
                      </span>
                    ));
                })()}
              </div>
              {(genogram?.members || []).flatMap(m => m.patterns || []).length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Add patterns to family members to see a summary of intergenerational themes.
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

// Wrapper component with error boundary
const GenogramPage = () => {
  return (
    <GenogramErrorBoundary>
      <GenogramPageContent />
    </GenogramErrorBoundary>
  );
};

export default GenogramPage;
